<template>
	<div class="song-widget" @dragover.prevent @drop="handleDrop">
		<b-alert v-if="error">{{error}}</b-alert>
		<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75" class="flex-fill overflow-hidden">
		<split-view>
			<template #first>
				<div ref="player" class="player"><div id="player"></div></div>
			</template>
			<template #second>
				<div class="songs flex-fill overflow-auto">
					<div class="container-fluid h-100">
						<div class="d-flex flex-row" v-if="history?.length > 0 && showHistory">
							<h5 class="my-2 flex-fill">History:</h5>
							<b-dropdown
								variant="link"
								no-caret
								right
								toggle-class="p-0"
								menu-class="shadow-sm"
							>
								<template #button-content>
									<b-icon icon="three-dots-vertical" style="cursor: pointer;"></b-icon>
								</template>
								<b-dropdown-item @click="showHistory = false">Hide history</b-dropdown-item>
								<b-dropdown-item @click="$bvModal.msgBoxConfirm('This will clear the play history.', { title: 'Please Confirm', size: 'sm', buttonSize: 'sm', okVariant: 'danger', footerClass: 'p-2', hideHeaderClose: false, centered: true }).then(value => value&&clearHistory())" :disabled="!queue?.length&&!currentSong&&!history?.length">Clear history</b-dropdown-item>
							</b-dropdown>
						</div>
						<b-list-group v-if="history?.length > 0 && showHistory">
							<b-list-group-item v-for="(song, idx) in history" :key="idx" class="song">
								<b-form-row>
									<b-col lg v-if="song?.info">
										<media-item :item="song">
											<template #dropdown>
												<b-dropdown-item @click="queueSong(song?.info?.canonical_url||song?.info?.url)"><b-icon-play/> Enqueue</b-dropdown-item>
											</template>
										</media-item>
									</b-col>
									<b-col lg>
										<show-more height="90">
											<chat-message :item="message" :serviceIcons="serviceIcons" v-for="(message, midx) in song?.messages" :key="midx"/>
										</show-more>
									</b-col>
								</b-form-row>
							</b-list-group-item>
						</b-list-group>
						<div v-if="currentSong">
							<h5 class="my-2">Now playing:</h5>
							<b-list-group class="p-0 mb-2">
								<b-list-group-item :class="['song', currentSong?.status ]">
									<b-form-row>
										<b-col lg v-if="currentSong?.info">
											<media-item :item="currentSong"/>
										</b-col>
										<b-col lg>
											<show-more height="90">
												<chat-message :item="message" :serviceIcons="serviceIcons" v-for="(message, midx) in currentSong?.messages" :key="midx"/>
											</show-more>
										</b-col>
									</b-form-row>
								</b-list-group-item>
							</b-list-group>
						</div>
						<div class="d-flex flex-row" v-if="queue?.length > 0">
							<h5 class="my-2 flex-fill">Up next:</h5>
							<b-dropdown
								variant="link"
								no-caret
								right
								toggle-class="p-0"
								menu-class="shadow-sm"
							>
								<template #button-content>
									<b-icon icon="three-dots-vertical" style="cursor: pointer;"></b-icon>
								</template>

								<b-dropdown-item @click="$bvModal.msgBoxConfirm('This will clear the play queue.', { title: 'Please Confirm', size: 'sm', buttonSize: 'sm', okVariant: 'danger', footerClass: 'p-2', hideHeaderClose: false, centered: true }).then(value => value&&clearQueue())" :disabled="!queue?.length&&!currentSong&&!history?.length">Clear queue</b-dropdown-item>
								<b-dropdown-item @click="sortQueue('value')">Sort by value</b-dropdown-item>
								<b-dropdown-item @click="sortQueue('time')">Sort by request time</b-dropdown-item>
							</b-dropdown>
						</div>
						<draggable v-model="queue" tag="b-list-group" class="p-0 list-group-striped" @end="onDragEnd">
							<b-list-group-item v-for="(song, idx) in queue" :key="idx" class="song">
								<b-form-row>
									<b-col lg v-if="song?.info">
										<media-item :item="song">
											<template #dropdown>
												<b-dropdown-item @click="removeSong(song.id)" variant="danger"><b-icon-trash/> Remove</b-dropdown-item>
												<b-dropdown-item @click="playSongById(song.id)"><b-icon-play/> Play</b-dropdown-item>
											</template>
										</media-item>
									</b-col>
									<b-col lg>
										<show-more height="90">
											<chat-message :item="message" :serviceIcons="serviceIcons" v-for="(message, midx) in song?.messages" :key="midx"/>
										</show-more>
									</b-col>
								</b-form-row>
							</b-list-group-item>
						</draggable>
						<div v-if="!queue?.length" class="text-center my-5">
							<p class="text-muted">Ready to take song requests</p>
						</div>
					</div>
				</div>
				<b-container fluid class="my-2">
					<b-alert v-model="showMessageError" :show="5" variant="danger" dismissible>{{messageError}}</b-alert>
					<b-form @submit.prevent="queueSong(message); message = ''">
						<b-input-group>
							<b-input-group-prepend>
								<b-button @click="playPause()" :disabled="!currentSong"><b-icon :icon="playerStatus == 'playing'?'pause-fill':'play-fill'"/></b-button>
								<b-button @click="playNextSong()" :disabled="!currentSong && !queue?.length"><b-icon icon="skip-end-fill"/></b-button>
								<b-dropdown no-caret dropup :disabled="!queue?.length && !history?.length && !currentSong">
									<template #button-content>
										<b-icon icon="three-dots-vertical" style="cursor: pointer;"></b-icon>
									</template>
									<b-dropdown-item @click="showHistory = !showHistory">{{showHistory?'Hide history':'Show history'}}</b-dropdown-item>
									<b-dropdown-item @click="$bvModal.msgBoxConfirm('This will clear the play queue, history and current song.', { title: 'Please Confirm', size: 'sm', buttonSize: 'sm', okVariant: 'danger', footerClass: 'p-2', hideHeaderClose: false, centered: true }).then(value => value&&clearAll())" :disabled="!queue?.length&&!currentSong&&!history?.length">Clear all</b-dropdown-item>
								</b-dropdown>
							</b-input-group-prepend>
							<b-input type="url" required v-model="message" placeholder="Add song..." :readonly="messageLoading>0" autocomplete="off"></b-input>
							<b-input-group-append>
								<b-button type="submit" variant="success" :disabled="!message || messageLoading > 0">
									<b-icon icon="arrow-right-circle-fill" />
								</b-button>
							</b-input-group-append>
						</b-input-group>
					</b-form>
				</b-container>
			</template>
		</split-view>
		<template #overlay>
			<div class="text-center p-4">
				<p class="mb-3">Song requests are available for subscribed users only.</p>
				<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
			</div>
		</template>
		</b-overlay>
	</div>
</template>

<script>
import util from '@/util';
import draggable from 'vuedraggable';
import giftThresholds from '@/GiftThresholds.js';
import ChatMessage from '@/components/ChatMessage.vue';
import ShowMore from '@/components/ShowMore.vue';
import MediaItem from '@/components/MediaItem.vue';
import SplitView from '@/components/SplitView.vue';
import '@/plugins/bootstrap-vue';

import YoutubePlayer from '@/player/YoutubePlayer';
import SoundcloudPlayer from '@/player/SoundcloudPlayer';
import SpotifyPlayer from '@/player/SpotifyPlayer';

const playerClasses = [
	YoutubePlayer,
	SoundcloudPlayer,
	SpotifyPlayer,
];

export default {
	data() { return {
		loading: 0,
		error: null,
		settings: null,
		history: [],
		currentSong: null,
		queue: [],
		showHistory: !localStorage.getItem('hideHistory'),
		user: {},
		showMessageError: null,
		messageError: null,
		messageLoading: 0,
		message: '',
		serviceIcons: {},
		giftThresholds,
		playerStatus: null,
	}},
	components: {
		draggable,
		ShowMore,
		ChatMessage,
		MediaItem,
		SplitView,
	},
	filters: {
		secondsToTime: util.secondsToTime,
		timeAgo: function(d) {
			return util.timeAgo(util.sqlStringToDate(d));
		},
		formatSQLDate: function(d) {
			let date = util.sqlStringToDate(d);
			return date ? date.toLocaleString() : '-';
		},
		money: util.moneyString,
	},
	mounted() {
		window.addEventListener("dragover", e => e.preventDefault());
		window.addEventListener("drop", this.handleDrop);
		window.addEventListener('keydown', this.handleKeyDown);

		// Update position state continuously
		// this.$refs.audio.ontimeupdate = () => {
		// 	if ("mediaSession" in navigator) {
		// 		navigator.mediaSession.setPositionState({
		// 			duration: this.$refs.audio.duration || 0,
		// 			playbackRate: this.$refs.audio.playbackRate || 1.0,
		// 			position: this.$refs.audio.currentTime || 0,
		// 		});
		// 	}
		// };
		this.updateMediaSession();
	},
	beforeUnmount() {
		window.removeEventListener("drop", this.handleDrop);
		window.removeEventListener('keydown', this.handleKeyDown);
	},
	async created() {
		try {
			await this.loadUserData();
			await this.reloadSongQueue(true);
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				widgetSettingsChange: msg => {
					if(msg.widgetType == 'song')
						this.loadWidgetSettings(msg.settings);
				},
				songAdded: () => {
					this.reloadSongQueue(true);
				},
			});
		} catch(e) {
			console.error(e);
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	watch: {
		currentSong: function(newValue, oldValue) {
			console.log('currentSong changed', newValue, oldValue);
			if(newValue?.info?.canonical_url != oldValue?.info?.canonical_url) {
				if(newValue?.info?.canonical_url) {
					this.playSong(newValue);
				} else {
					if(this.player) {
						this.player.cleanup();
						this.playerIdx = -1;
						this.player = null;
					}
				}
				console.log('song changed', newValue?.info?.canonical_url, oldValue?.info?.canonical_url);
			}
		},
		showHistory: function(newValue) {
			if(newValue) localStorage.removeItem('hideHistory');
			else localStorage.setItem('hideHistory', 'true');
		},
	},
	methods: {
		async loadUserData() {
			try {
				this.loading++;
				this.user = await this.$api.get(`userData`);
				const services = await this.$api.get(`services`);
				this.serviceIcons = Object.fromEntries(services.filter(s => s.icon && s.icon.url).map(s => ([ s.id, s.icon.url ])));
			} catch(e) {
				console.error('Loading user data', e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		loadWidgetSettings(settings) {
			this.settings = settings;
		},
		async playNextSong(status) {
			try {
				this.loading++;
				await this.$api.post(`song/${this.currentSong.id}/status`, { status: status||'finished' });
				await this.reloadSongQueue();
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could play next song: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				this.loading--;
			}
		},
		async playSongById(id) {
			try {
				this.loading++;
				await this.$api.post(`song/${id}/status`, { status: 'playing' });
				await this.reloadSongQueue();
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could play next song: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				this.loading--;
			}
		},
		async reloadSongQueue() {
			try {
				this.loading++;
				let songs = await this.$api.get(`songs`);
				console.log('loaded songs', songs);
				songs = songs.map(s => ({
					...s,
					messages: (s.messages||[]).filter(m => !!m),
					valueString: s.value && util.generateValueString(s.value, s.currency_code),
					expanded: false,
				}));

				this.history = songs.filter(s => s.status == 'finished' || s.status == 'error');
				this.currentSong = songs.find(s => s.status == 'playing');
				this.queue = songs.filter(s => s.status == 'ready');
				this.updateMediaSession();
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async onDragEnd(evt) {
			try {
				this.loading++;
				const item = this.queue[evt.newIndex];
				if(evt.newIndex == evt.oldIndex) return;
				await this.$api.post(`song/${encodeURIComponent(item.id)}/reorder`, { after: evt.newIndex == 0 ? this.currentSong?.id : this.queue[evt.newIndex - 1].id });
			} catch(e) {
				console.error('Could not reorder songs', e);
				this.$bvToast.toast('Could not reorder songs: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async playSong(song) {
			const playerClassIdx = playerClasses.findIndex(c => c.urlMatches(song.info.canonical_url));
			if(playerClassIdx < 0) {
				this.playNextSong();
				return;
			}
			if(playerClassIdx != this.playerIdx) {
				if(this.player) {
					await this.player.cleanup();
					this.playerIdx = -1;
					this.player = null;
				}
				this.player = new playerClasses[playerClassIdx]('player');
				const statusHandler = async ev => {
					this.playerStatus = ev.detail.status;
					if(ev.detail.status == 'finished') {
						this.player.removeEventListener('status', statusHandler);
						this.player.removeEventListener('error', errorHandler);
					}

					try {
						if(ev.detail.status == 'finished')
							this.playNextSong();
					} catch(e) {
						console.error('Could not play song', e);
					}
				};
				this.player.addEventListener('status', statusHandler);
				const errorHandler = async ev => {
					console.error('player error', ev.detail);
					this.player.removeEventListener('error', errorHandler);
					this.player.removeEventListener('status', statusHandler);
					setTimeout(() => this.playNextSong('error'), 5 * 1000);
				};
				this.player.addEventListener('error', errorHandler);
			}

			try {
				console.log('Loading song', song.info.url, song.info?.start_time, song.timeLimit);
				await this.player.load(song.info.url, song.info?.start_time, song.timeLimit);
				await this.player.play();
			} catch(e) {
				console.error('error while initializing', e);
			}
		},
		async queueSong(url) {
			try {
				this.showMessageError = false;
				this.messageError = null;
				this.messageLoading++;
				await this.$api.post(`song`, { url });
			} catch(e) {
				console.error(e);
				this.showMessageError = true;
				this.messageError = e.message;
			} finally {
				this.messageLoading--;
			}
		},
		async removeSong(id) {
			try {
				this.loading++;
				await this.$api.delete(`song/${encodeURIComponent(id)}`);
			} catch(e) {
				console.error('Could not remove song', id, e);
				this.$bvToast.toast(`Could not remove song ${id}: ${e.error||e.toString()}`, {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async clearHistory() {
			try {
				this.loading++;
				await this.$api.delete(`songs/history`);
			} catch(e) {
				this.$bvToast.toast('Could not clear queue: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async clearQueue() {
			try {
				this.loading++;
				await this.$api.delete(`songs/queue`);
			} catch(e) {
				this.$bvToast.toast('Could not clear queue: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async clearAll() {
			try {
				this.loading++;
				await this.$api.delete(`songs`);
			} catch(e) {
				this.$bvToast.toast('Could not clear queue: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async sortQueue(by) {
			try {
				this.loading++;
				await this.$api.post(`songs/sort`, { by });
			} catch(e) {
				this.$bvToast.toast('Could not sort queue: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				await this.reloadSongQueue();
				this.loading--;
			}
		},
		async playPause() {
			try {
				this.loading++;
				if(this.playerStatus == 'playing')
					await this.player?.pause();
				else
					await this.player?.play();
				this.updateMediaSession();
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not toggle pause: '+(e.error||e.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			} finally {
				this.loading--;
			}
		},
		handleDrop(event) {
			event.preventDefault();
			event.stopPropagation();
			console.log('handleDrop', event, event.dataTransfer);
			const data = event.dataTransfer.getData("text/uri-list")  || event.dataTransfer.getData("text/plain");
			console.log('data', data);
			if(!data) return;
			try {
				new URL(data);
				this.queueSong(data);
			} catch (e) {
				console.warn("Dropped data is not a valid URL:", data);
			}
		},
		handleKeyDown(event) {
			if(event.code === 'MediaTrackNext')
				this.playNextSong();
			else if(event.code == 'MediaPlayPause')
				this.playPause();
			else return;
			event.preventDefault();
			event.stopPropagation();
		},
		updateMediaSession() {
			console.log('update media session');
			if(!('mediaSession' in navigator)) return;
			navigator.mediaSession.metadata = new window.MediaMetadata({
				title: this.currentSong?.info?.title,
				artist: this.currentSong?.info?.author,
				album: "LiveJoiner Song Requests",
				artwork: this.currentSong?.info?.thumbnail && [ { src: this.currentSong?.info?.thumbnail } ],
			});

			// navigator.mediaSession.setActionHandler("play", () => this.playPause(true));
			// navigator.mediaSession.setActionHandler("pause", () => this.playPause(false));
			// navigator.mediaSession.setActionHandler("previoustrack", () => this.playPreviousSong());
			navigator.mediaSession.setActionHandler("nexttrack", () => this.playNextSong());

			// navigator.mediaSession.setActionHandler("seekbackward", (details) => {
			// 	const skip = details.seekOffset || 10;
			// 	this.seekBackward(skip);
			// });

			// navigator.mediaSession.setActionHandler("seekforward", (details) => {
			// 	const skip = details.seekOffset || 10;
			// 	this.seekForward(skip);
			// });

			// navigator.mediaSession.setActionHandler("seekto", (details) => {
			// 	if(details.fastSeek && "fastSeek" in this.$refs.audio) {
			// 		this.$refs.audio.fastSeek(details.seekTime);
			// 	} else {
			// 		this.seekTo(details.seekTime);
			// 	}
			// });

			// // Keep position info in sync for OS-level scrubbers
			// navigator.mediaSession.setPositionState({
			// 	duration: this.$refs.audio.duration || 0,
			// 	playbackRate: this.$refs.audio.playbackRate || 1.0,
			// 	position: this.$refs.audio.currentTime || 0,
			// });
		},
	},
}
</script>

<style lang="scss">
.song-widget {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	overflow: hidden;

	.pane1 {
		display: flex;
		flex-direction: column;
		overflow: hidden;

		> .player {
			flex: 1 1 auto;
			display: flex;

			#player {
				flex: 1 1 100%;
				display: block;
				width: 100%;
				height: 100%;
			}
		}
	}
}
</style>
